package org.teddapp.project.ui.admin

import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.server.ServerPublication
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddCardTone
import org.teddapp.project.ui.TeddEmptyState
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.TeddVisibility
import org.teddapp.project.ui.TeddVisibilityBadge
import org.teddapp.project.ui.projects.MenuAction
import org.teddapp.project.ui.projects.ProjectMoreMenu

// ──────────────────────────────────────────────────────────────────────
// Les rubriques du site : une à la fois, le français et l'anglais côte à
// côte, et les gestes de chaque contenu à sa place. En fenêtre large, un
// tableau ; sur téléphone, des cartes.
// ──────────────────────────────────────────────────────────────────────

private val SITE_LANGS = listOf("fr", "en")

/** L'adresse d'une publication sur le site, comme le site la construit. */
internal fun sitePath(p: ServerPublication): String = when (p.source_kind) {
    "note" -> "/${p.lang}/blog/${p.slug}"
    "portfolio_piece" -> "/${p.lang}/portfolio/${p.slug}"
    else -> "/${p.lang}/${p.slug}"
}

@Composable
private fun RubricHint(text: String) = Text(text, color = TeddTokens.InkMuted, style = TeddType.Caption)

/** Deux cases, l'une à côté de l'autre sur un écran large, l'une sous l'autre sur un téléphone. */
@Composable
private fun LangPair(compact: Boolean, cell: @Composable (lang: String, modifier: Modifier) -> Unit) {
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) { SITE_LANGS.forEach { cell(it, Modifier.fillMaxWidth()) } }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S4), verticalAlignment = Alignment.Top) {
            SITE_LANGS.forEach { cell(it, Modifier.weight(1f)) }
        }
    }
}

/** Les gestes sur une publication, dans une ligne : modifier (ou l'aperçu), puis le reste dans « … ». */
@Composable
private fun RowActions(model: AdminModel, p: ServerPublication, editable: Boolean, onEdit: () -> Unit, extra: List<MenuAction> = emptyList()) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
        if (editable) TeddButton(text = "Modifier", onClick = onEdit, size = TeddButtonSize.Sm, icon = "edit", enabled = !model.busy)
        else TeddButton(text = "Aperçu", onClick = { model.openPreview(p) }, size = TeddButtonSize.Sm, kind = TeddButtonKind.Ghost, icon = "eye")
        val more = buildList {
            if (editable) add(MenuAction("Aperçu", "eye", { model.openPreview(p) }))
            if (!p.isPage) add(if (p.featured) MenuAction("Retirer de l'accueil", "star", { model.setFeatured(p, false) }) else MenuAction("Mettre sur l'accueil", "star", { model.setFeatured(p, true) }))
            if (p.online) add(MenuAction("Retirer du site", "x", { model.confirmUnpublish = p }))
            else add(MenuAction("Remettre en ligne", "globe", { model.republish(p) }))
            addAll(extra)
        }
        if (more.isNotEmpty()) ProjectMoreMenu(more)
    }
}

// ── Accueil et À propos : une page par langue ──────────────────────────

@Composable
internal fun RolePanel(model: AdminModel, role: String, compact: Boolean) {
    val pages = model.publications.orEmpty().filter { it.isPage && it.role == role }
    if (compact) {
        LangPair(true) { lang, modifier ->
            val page = pages.firstOrNull { it.lang == lang }
            val other = pages.firstOrNull { it.lang != lang }
            RoleSlot(model, role, lang, page, other, modifier)
        }
        if (role == "home") FeaturedLists(model)
        return
    }
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S5)) {
        RoleTable(model, role, pages)
        if (role == "home") FeaturedLists(model)
    }
}

/** Les projets et les articles choisis pour l'accueil, dans l'ordre où le site les montre. */
@Composable
private fun FeaturedLists(model: AdminModel) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S4)) {
        listOf("portfolio_piece" to "PROJETS SUR L'ACCUEIL", "note" to "ARTICLES SUR L'ACCUEIL").forEach { (kind, title) ->
            val chosen = model.featured(kind)
            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                Text("$title · ${chosen.size}/3", color = TeddTokens.InkMuted, style = TeddType.Overline)
                if (chosen.isEmpty()) RubricHint(
                    if (kind == "note") "Aucun article choisi : l'accueil montre les trois plus récents. Choisis-en depuis le Blog, menu « … » → Mettre sur l'accueil."
                    else "Aucun projet choisi : l'accueil montre les trois plus récents. Choisis-en depuis Portfolio, menu « … » → Mettre sur l'accueil.",
                )
                else if (chosen.size > 3) RubricHint("Seuls les trois premiers sont montrés.")
                chosen.forEachIndexed { index, p ->
                    TeddCard(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(TeddTokens.RMd), padding = PaddingValues(horizontal = TeddSpace.S4, vertical = TeddSpace.S2)) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
                            Text("${index + 1}", color = if (index < 3) TeddTokens.LimeStrong else TeddTokens.InkSubtle, style = TeddType.MonoSm)
                            Column(Modifier.weight(1f)) {
                                Text(p.title, color = TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 1, overflow = TextOverflow.Ellipsis)
                                Text(p.lang.uppercase() + " · " + sitePath(p), color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis)
                            }
                            if (!p.online) TeddTag("Pas en ligne")
                            ProjectMoreMenu(
                                listOfNotNull(
                                    if (index > 0) MenuAction("Monter", "arrow-up", { model.moveFeatured(p, -1) }) else null,
                                    if (index < chosen.lastIndex) MenuAction("Descendre", "arrow-down", { model.moveFeatured(p, 1) }) else null,
                                    MenuAction("Retirer de l'accueil", "x", { model.setFeatured(p, false) }),
                                ),
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun RoleTable(model: AdminModel, role: String, pages: List<ServerPublication>) {
    AdminTable(
        columns = listOf(
            TableColumn("Langue", width = 88.dp),
            TableColumn("Page", 1f),
            TableColumn("État", width = 120.dp),
            TableColumn("Modifié", width = 128.dp),
            TableColumn("", width = 176.dp),
        ),
        rows = SITE_LANGS.map { lang ->
            val page = pages.firstOrNull { it.lang == lang }
            val other = pages.firstOrNull { it.lang != lang }
            listOf(
                { Text(if (lang == "en") "English" else "Français", color = TeddTokens.Ink, style = TeddType.BodyStrong) },
                {
                    if (page != null) TitleCell(page.title, "/${page.lang}/${page.slug}")
                    else TitleCell(
                        "Pas encore écrite",
                        if (other != null) "la version ${langName(other.lang)}e existe : celle-ci lui sera reliée" else "le site n'a pas cette page dans cette langue",
                        muted = true,
                    )
                },
                { if (page != null) StateBadge(page) else TeddTag("À écrire") },
                { MonoCell(if (page != null) formatDay(page.published_at_ms) else "—") },
                {
                    if (page != null) RowActions(model, page, editable = true, onEdit = { model.openEditor(page) })
                    else TeddButton(
                        text = "Rédiger en " + langName(lang),
                        onClick = { model.newDraft("page", lang, role = role, translationOf = other?.id) },
                        size = TeddButtonSize.Sm,
                        kind = TeddButtonKind.Primary,
                        icon = "plus",
                        enabled = !model.busy,
                    )
                },
            )
        },
    )
}

@Composable
private fun RoleSlot(model: AdminModel, role: String, lang: String, page: ServerPublication?, other: ServerPublication?, modifier: Modifier) {
    TeddCard(
        modifier = modifier,
        title = if (lang == "en") "English" else "Français",
        tone = if (page == null) TeddCardTone.Sunken else TeddCardTone.Default,
        padding = PaddingValues(TeddSpace.S5),
        action = {
            Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) { if (page != null) StateBadge(page) else TeddTag("Pas encore écrite") }
        },
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            if (page != null) {
                Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                    Text(page.title, color = TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 2, overflow = TextOverflow.Ellipsis)
                    Text("/${page.lang}/${page.slug} · ${formatDay(page.published_at_ms)}", color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis)
                }
                RowActions(model, page, editable = true, onEdit = { model.openEditor(page) })
            } else {
                RubricHint(
                    if (other != null) "La version ${langName(other.lang)}e existe : celle-ci lui sera reliée."
                    else "Le site n'a pas encore cette page dans cette langue.",
                )
                TeddButton(
                    text = "Rédiger en " + langName(lang),
                    onClick = { model.newDraft("page", lang, role = role, translationOf = other?.id) },
                    size = TeddButtonSize.Sm,
                    kind = TeddButtonKind.Primary,
                    icon = "plus",
                    enabled = !model.busy,
                )
            }
        }
    }
}

// ── Portfolio et Blog : chaque contenu et sa traduction ────────────────

/** Les deux versions d'un contenu partagent une clé ; les plus récents d'abord. */
private fun groupsOf(model: AdminModel, kind: String): List<List<ServerPublication>> =
    model.publications.orEmpty().filter { it.source_kind == kind }
        .groupBy { it.translation_key.ifEmpty { it.id } }.values
        .filter { g -> g.any { model.shows(it) } }
        .sortedByDescending { g -> g.maxOf { it.published_at_ms } }

@Composable
internal fun PairedPanel(model: AdminModel, kind: String, compact: Boolean) {
    val groups = groupsOf(model, kind)
    val all = model.publications.orEmpty().filter { it.source_kind == kind }
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        FilterBar(model, all, compact)
        if (groups.isEmpty()) {
            if (model.form == null) TeddEmptyState(
                title = when {
                    all.isNotEmpty() -> "Rien ne répond à ce filtre"
                    kind == "note" -> "Aucun article"
                    else -> "Aucune pièce publiée"
                },
                message = if (all.isNotEmpty()) "Change de filtre, ou efface la recherche." else "Rien de cette rubrique n'est encore sur le site.",
                icon = "globe",
            )
            return@Column
        }
        if (compact) {
            groups.forEach { group -> PairedCard(model, kind, group) }
        } else {
            AdminTable(
                columns = listOf(
                    TableColumn(if (kind == "note") "Article" else "Projet", 1.8f),
                    TableColumn("Français", 1f),
                    TableColumn("Anglais", 1f),
                    TableColumn("Modifié", width = 128.dp),
                    TableColumn("", width = 150.dp),
                ),
                rows = groups.map { group -> pairedRow(model, kind, group) },
            )
        }
        if (kind == "note") FlowBanner()
    }
}

private fun pairedRow(model: AdminModel, kind: String, group: List<ServerPublication>): List<@Composable () -> Unit> {
    val first = group.firstOrNull { it.lang == "fr" } ?: group.first()
    val latest = group.maxBy { it.published_at_ms }
    val detail = buildList {
        if (group.any { it.featured }) add("★ accueil")
        if (first.example) add("exemple")
        first.series?.let { add("série « ${it.title} » · épisode ${it.episode}") }
        add(sitePath(first))
    }.joinToString(" · ")
    return listOf(
        { TitleCell(first.title, detail) },
        { LangState(model, kind, "fr", group) },
        { LangState(model, kind, "en", group) },
        { MonoCell(formatDay(latest.published_at_ms)) },
        {
            // Tout s'ouvre dans l'éditeur : un contenu venu de l'appareil s'en détache à la première réécriture.
            RowActions(
                model, first,
                editable = true,
                onEdit = { model.openEditor(first) },
                extra = group.filter { it.id != first.id }.flatMap { p ->
                    listOfNotNull(
                        MenuAction("Modifier la version ${langName(p.lang)}e", "edit", { model.openEditor(p) }),
                        if (p.online) MenuAction("Retirer la version ${langName(p.lang)}e", "x", { model.confirmUnpublish = p })
                        else MenuAction("Remettre en ligne la version ${langName(p.lang)}e", "globe", { model.republish(p) }),
                    )
                },
            )
        },
    )
}

/** L'état d'une version dans sa colonne : son badge et son adresse, ou le geste pour l'ajouter. */
@Composable
private fun LangState(model: AdminModel, kind: String, lang: String, group: List<ServerPublication>) {
    val p = group.firstOrNull { it.lang == lang }
    val other = group.firstOrNull { it.lang != lang }
    if (p != null) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1), horizontalAlignment = Alignment.Start) {
            androidx.compose.foundation.layout.FlowRow(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S1), verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) { StateBadge(p) }
            Text(p.slug, color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
    } else if (other != null) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1), horizontalAlignment = Alignment.Start) {
            TeddTag("À traduire")
            TeddButton(
                text = "Traduire",
                onClick = { model.translate(other, lang) },
                size = TeddButtonSize.Sm,
                kind = TeddButtonKind.Ghost,
                icon = "plus",
                enabled = !model.busy,
            )
        }
    } else {
        TeddTag("Pas de version")
    }
}

@Composable
private fun PairedCard(model: AdminModel, kind: String, group: List<ServerPublication>) {
    val title = (group.firstOrNull { it.lang == "fr" } ?: group.first()).title
    TeddCard(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(TeddTokens.RMd), padding = PaddingValues(TeddSpace.S4)) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            Text(title, color = TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 2, overflow = TextOverflow.Ellipsis)
            LangPair(true) { lang, modifier ->
                val p = group.firstOrNull { it.lang == lang }
                LangCell(model, kind, lang, p, group.firstOrNull { it.lang != lang }, title, modifier)
            }
        }
    }
}

@Composable
private fun LangCell(model: AdminModel, kind: String, lang: String, p: ServerPublication?, other: ServerPublication?, groupTitle: String, modifier: Modifier) {
    TeddCard(
        modifier = modifier,
        shape = RoundedCornerShape(TeddTokens.RSm),
        tone = TeddCardTone.Sunken,
        padding = PaddingValues(horizontal = TeddSpace.S3, vertical = TeddSpace.S3),
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddTag(lang.uppercase())
                if (p != null) {
                    Text(sitePath(p), color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
                    StateBadge(p)
                } else {
                    Text("Pas de version", color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.weight(1f))
                }
            }
            if (p != null) {
                // Le titre de la carte est celui de la version française : on ne le répète pas.
                if (p.title != groupTitle) Text(p.title, color = TeddTokens.Ink, style = TeddType.Body, maxLines = 1, overflow = TextOverflow.Ellipsis)
                p.series?.let { s ->
                    Text("Série « ${s.title} » · épisode ${s.episode}", color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1, overflow = TextOverflow.Ellipsis)
                }
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    Text(formatDay(p.published_at_ms), color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.weight(1f))
                    RowActions(model, p, editable = true, onEdit = { model.openEditor(p) })
                }
            } else if (other != null) {
                TeddButton(
                    text = "Ajouter la version " + langName(lang) + "e",
                    onClick = { model.translate(other, lang) },
                    size = TeddButtonSize.Sm,
                    icon = "plus",
                    enabled = !model.busy,
                )
            }
        }
    }
}

// ── Autres pages ───────────────────────────────────────────────────────

@Composable
internal fun OtherPanel(model: AdminModel, compact: Boolean) {
    val all = model.publications.orEmpty().filter(::isOther)
    val others = all.filter { model.shows(it) }
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        FilterBar(model, all, compact)
        if (others.isEmpty()) {
            if (model.form == null) TeddEmptyState(
                title = if (all.isNotEmpty()) "Rien ne répond à ce filtre" else "Aucune autre page",
                message = if (all.isNotEmpty()) "Change de filtre, ou efface la recherche." else "Une page libre a sa propre adresse, hors des rubriques.",
                icon = "globe",
            )
            return@Column
        }
        if (compact) {
            Column(modifier = Modifier.padding(top = TeddSpace.S1), verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                others.forEach { p -> PublicationCard(model, p, compact) }
            }
        } else {
            AdminTable(
                columns = listOf(
                    TableColumn("Page", 1f),
                    TableColumn("Langue", width = 64.dp),
                    TableColumn("État", width = 120.dp),
                    TableColumn("Modifié", width = 128.dp),
                    TableColumn("", width = 176.dp),
                ),
                rows = others.map { p ->
                    listOf(
                        { TitleCell(p.title, "/${p.lang}/${p.slug} · ${kindLabel(p.source_kind)}") },
                        { TeddTag(p.lang.uppercase()) },
                        { StateBadge(p) },
                        { MonoCell(formatDay(p.published_at_ms)) },
                        { RowActions(model, p, editable = true, onEdit = { model.openEditor(p) }) },
                    )
                },
            )
        }
    }
}

// ── Médias ─────────────────────────────────────────────────────────────

/** La galerie : les portraits, puis toutes les images envoyées au serveur, en vignettes. */
@Composable
internal fun MediaPanel(model: AdminModel, compact: Boolean) {
    Column(verticalArrangement = Arrangement.spacedBy(if (compact) TeddSpace.S4 else TeddSpace.S5)) {
        GalleryGrid(model, compact)
        PhotosCard(model, compact)
    }
}

// ── Exemples ───────────────────────────────────────────────────────────

/** Semer des exemples dans chaque rubrique, puis retirer ce qui n'a pas été remplacé. */
@Composable
internal fun ExamplesCard(model: AdminModel) {
    val present = model.publications.orEmpty().count { it.example }
    TeddCard(modifier = Modifier.fillMaxWidth(), title = "Exemples", icon = "edit", padding = PaddingValues(TeddSpace.S5)) {
        Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
            RubricHint(
                "Remplit chaque rubrique d'exemples en français et en anglais : accueil, à propos, trois pièces de portfolio, un article de blog et une série de trois épisodes. " +
                    "Tous partent en ligne, marqués « Exemple » : le site se montre complet, et tu remplaces chaque exemple par ton contenu à ton rythme. " +
                    "Une page d'exemple que tu réécris devient la tienne ; une pièce ou un article d'exemple se retire quand le tien est en ligne.",
            )
            RubricHint(if (present == 0) "Aucun exemple sur le serveur." else "$present exemple${if (present > 1) "s" else ""} sur le serveur.")
            Row(modifier = Modifier.horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                TeddButton(text = "Remplir avec des exemples", onClick = model::fillExamples, icon = "plus", enabled = !model.busy)
                if (present > 0) TeddButton(
                    text = "Retirer les exemples",
                    onClick = { model.confirmRemoveExamples = true },
                    kind = TeddButtonKind.Ghost,
                    enabled = !model.busy,
                )
            }
        }
    }
}
